import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { TextArea } from "@/components/TextArea/TextArea";
import { classNames } from "@/utils/classNames";

import {
	Tier,
	TIERS,
} from "@/pages/Private/pages/OnboardingTour/components/steps/scoring/criteriaTiers";

import { ScoreCriteriaType } from "../../schema/score.schema";

import { findNonDiscriminatingBuckets } from "./rubricBucketWarnings";
import {
	aiCriterionOf,
	CriterionRef,
	criterionRefKey,
	isTwoBucketAi,
	readAiText,
	TWO_BUCKET_POINTS,
	writeAiText,
} from "./criteriaDraft";

interface AiCriterionEditorProps {
	scoring: ScoreCriteriaType;
	criterionRef: CriterionRef;
	onChange: (next: ScoreCriteriaType) => void;
}

const DOT: Record<Tier, string> = {
	3: "bg-status-success",
	2: "bg-status-warning",
	1: "bg-status-warning",
	0: "bg-status-error",
};

/**
 * The free-text buckets of an AI criterion (PREC-4055). A criterion created
 * here has two — what scores higher and what disqualifies — side by side; a
 * legacy criterion with 1- or 2-point buckets keeps its four, one per row, so
 * nothing it grades today is lost. Buckets that state the same consequence
 * are flagged, because the model cannot tell them apart (PREC-3788).
 */
export const AiCriterionEditor: FunctionComponent<AiCriterionEditorProps> = ({
	scoring,
	criterionRef,
	onChange,
}) => {
	const { t } = useTranslation();
	const other = aiCriterionOf(scoring, criterionRef);

	if (!other) {
		return null;
	}

	const twoBuckets = isTwoBucketAi(other);
	const points: readonly Tier[] = twoBuckets ? TWO_BUCKET_POINTS : TIERS;
	const flagged = findNonDiscriminatingBuckets(other.criteria);
	const refKey = criterionRefKey(criterionRef);

	const labelOf = (tier: Tier) =>
		twoBuckets
			? t(tier === 3 ? "leadScoring.scoringPage.scoreHigher" : "leadScoring.scoringPage.disqualify")
			: t(`leadScoring.scoringPage.tiers.${tier}`);

	const placeholderOf = (tier: Tier) =>
		tier === 3
			? t("leadScoring.scoringPage.scoreHigherPlaceholder")
			: tier === 0
				? t("leadScoring.scoringPage.disqualifyPlaceholder")
				: t("leadScoring.scoringPage.bucketPlaceholder", { tier: labelOf(tier) });

	return (
		<div
			className={classNames(
				"grid gap-3",
				twoBuckets ? "grid-cols-1 tb:grid-cols-2" : "grid-cols-1"
			)}
			data-testid="ai-editor"
		>
			{points.map((tier) => (
				<div key={tier} className="flex flex-col gap-1.5">
					<div className="flex items-center gap-1.5 text-xs font-bold text-text">
						<span className={classNames("h-2 w-2 shrink-0 rounded-full", DOT[tier])} />
						{labelOf(tier)}
					</div>
					<TextArea
						className={classNames("min-h-[96px]", flagged.has(tier) && "border-status-warning")}
						handleChange={(event) =>
							onChange(writeAiText(scoring, criterionRef, tier, event.target.value))
						}
						name={`ai-${refKey}-${tier}`}
						placeholder={placeholderOf(tier)}
						showError={false}
						value={readAiText(scoring, criterionRef, tier)}
					/>
					{flagged.has(tier) && (
						<p
							className="flex items-start gap-1.5 text-[11px] text-tag-warning-dark"
							data-testid={`ai-duplicate-${tier}`}
						>
							<Icon className="mt-0.5 h-3 w-3 shrink-0" icon={faTriangleExclamation} />
							{t("leadScoring.scoringPage.duplicateBucket")}
						</p>
					)}
				</div>
			))}
		</div>
	);
};
